import React, { useState, useEffect } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Calendar, Plus, Loader2, MapPin, Clock, Users, Edit2, Trash2, Eye, EyeOff } from 'lucide-react';
import axios from '../lib/axios';
import { toast } from 'sonner';
import { useAuth, API } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { DashboardContainer, PageHeader, FilterChips } from '../components/DashboardLayout';

const MyEventsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, upcoming, past

  useEffect(() => {
    if (user) {
      fetchMyEvents();
    }
  }, [user]);

  const fetchMyEvents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/events/user/my`);
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load your events');
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success('Event deleted');
      setEvents(events.filter(e => e.id !== eventId));
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  const now = new Date();
  const filteredEvents = events.filter(event => {
    if (filter === 'all') return true;
    const eventDate = new Date(event.event_date);
    if (filter === 'upcoming') return eventDate >= now;
    if (filter === 'past') return eventDate < now;
    return true;
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (event) => {
    const eventDate = new Date(event.event_date);
    const isPast = eventDate < now;
    
    if (event.status === 'pending') {
      return <span className="px-2 py-1 bg-amber-500/20 text-amber-500 text-xs font-medium rounded-full">Pending Approval</span>;
    }
    if (isPast) {
      return <span className="px-2 py-1 bg-gray-500/20 text-gray-500 text-xs font-medium rounded-full">Past</span>;
    }
    return <span className="px-2 py-1 bg-green-500/20 text-green-500 text-xs font-medium rounded-full">Active</span>;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <DashboardContainer>
        {/* Standardized Header */}
        <PageHeader pageKey="my-events">
          <Link
            to="/events/create"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Create Event</span>
          </Link>
        </PageHeader>

        {/* Filters */}
        <FilterChips
          options={[
            { key: 'all', label: 'All' },
            { key: 'upcoming', label: 'Upcoming' },
            { key: 'past', label: 'Past' }
          ]}
          activeFilter={filter}
          onFilterChange={setFilter}
        />

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredEvents.length === 0 && (
          <div className="bg-card rounded-2xl p-12 border border-border text-center">
            <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              {filter === 'all' ? 'No events yet' : `No ${filter} events`}
            </h2>
            <p className="text-muted-foreground mb-6">
              {filter === 'all' 
                ? "You haven't created any events yet. Start organizing!" 
                : `You don't have any ${filter} events.`}
            </p>
            <Link
              to="/events/create"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl hover:opacity-90 transition-opacity"
            >
              <Plus className="w-4 h-4" />
              Create Your First Event
            </Link>
          </div>
        )}

        {/* Events List */}
        {!loading && filteredEvents.length > 0 && (
          <div className="space-y-6">
            {filteredEvents.map(event => {
              const eventDate = new Date(event.event_date);
              const isPast = eventDate < now;
              
              return (
                <div
                  key={event.id}
                  className={`bg-card rounded-2xl border border-border overflow-hidden transition-all hover:border-primary/30 hover:shadow-lg ${
                    isPast ? 'opacity-70' : ''
                  }`}
                >
                  <div className="flex gap-5 p-5">
                    {/* Image */}
                    <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                      {event.images?.[0] || event.image ? (
                        <img
                          src={event.images?.[0] || event.image}
                          alt={event.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-4xl bg-primary/10">
                          📅
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <Link 
                          to={`/events/${event.slug || event.id}`}
                          className="font-semibold text-lg text-foreground hover:text-primary transition-colors line-clamp-2"
                        >
                          {event.title}
                        </Link>
                        {getStatusBadge(event)}
                      </div>

                      <div className="space-y-2 text-sm text-muted-foreground mb-4">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 flex-shrink-0" />
                          <span>{formatDate(event.event_date)} at {formatTime(event.event_date)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">
                            {event.location?.venue_name || event.location?.address || event.location?.island}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 flex-shrink-0" />
                          <span>{event.going_count || 0} going · {event.interested_count || 0} interested</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-3 flex-wrap">
                        <Link
                          to={`/events/${event.slug || event.id}`}
                          className="flex items-center gap-1.5 px-4 py-2 bg-secondary text-foreground rounded-xl text-sm font-medium hover:bg-secondary/80 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </Link>
                        <Link
                          to={`/events/${event.slug || event.id}/edit`}
                          className="flex items-center gap-1.5 px-4 py-2 bg-primary/10 text-primary rounded-xl text-sm font-medium hover:bg-primary/20 transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </Link>
                        <button
                          onClick={() => deleteEvent(event.id)}
                          className="flex items-center gap-1.5 px-4 py-2 bg-red-500/10 text-red-500 rounded-xl text-sm font-medium hover:bg-red-500/20 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Ticket Stats (if event has tickets) */}
                  {event.tickets && event.tickets.length > 0 && (
                    <div className="border-t border-border px-5 py-4 bg-secondary/30">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Tickets sold:</span>
                        <span className="font-semibold text-foreground">
                          {event.tickets.reduce((sum, t) => sum + (t.sold || 0), 0)} / {event.tickets.reduce((sum, t) => sum + t.quantity, 0)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DashboardContainer>
      <Footer />
    </div>
  );
};

export default MyEventsPage;
